import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Box,
    Collapse
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LocalShippingOutlined from "@material-ui/icons/LocalShippingOutlined";
import PlaceOutlined from "@material-ui/icons/PlaceOutlined";
import ExpandMore from "@material-ui/icons/ExpandMore";
import Timeline from "@material-ui/icons/Timeline";
import { getOrderStatusLabel } from "../../utils/orderStatusLabels";

const POLL_INTERVAL_MS = 10000;

function formatTime(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
}

const useStyles = makeStyles((theme) => ({
    root: {
        "& .MuiDialog-paper": {
            borderRadius: 16,
            overflow: "hidden",
            maxWidth: 420,
        },
    },
    title: {
        padding: "20px 24px 12px",
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
        display: "flex",
        alignItems: "center",
        gap: 10,
        "& h2": {
            fontSize: "1.15rem",
            fontWeight: 600,
        },
    },
    content: {
        padding: "16px 24px 24px",
    },
    section: {
        marginBottom: 16,
        "&:last-of-type": { marginBottom: 0 },
    },
    sectionTitle: {
        fontSize: "0.7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: theme.palette.text.secondary,
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
    refRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 12,
    },
    refCode: {
        fontFamily: "monospace",
        fontWeight: 600,
        fontSize: "0.95rem",
    },
    statusChip: {
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: "0.75rem",
        fontWeight: 600,
    },
    statusActive: {
        background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
        color: "#1565c0",
    },
    statusDone: {
        background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
        color: "#2e7d32",
    },
    statusCancel: {
        background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
        color: "#c62828",
    },
    card: {
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        "&:last-child": { marginBottom: 0 },
    },
    cardPickup: {
        background: "linear-gradient(135deg, #e8f4fd 0%, #d6ebfa 100%)",
        border: "1px solid rgba(33, 150, 243, 0.2)",
    },
    cardDelivery: {
        background: "linear-gradient(135deg, #e8f5e9 0%, #dcedc8 100%)",
        border: "1px solid rgba(76, 175, 80, 0.2)",
    },
    cardLabel: {
        fontSize: "0.7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "rgba(0,0,0,0.55)",
        marginBottom: 6,
    },
    contactName: {
        fontWeight: 600,
        fontSize: "0.9rem",
        marginBottom: 2,
    },
    address: {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    phone: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        marginTop: 4,
    },
    costRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 14px",
        borderRadius: 10,
        background: "rgba(0,0,0,0.04)",
    },
    costLabel: {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
    },
    costValue: {
        fontWeight: 700,
        fontSize: "1rem",
    },
    trackingLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginTop: 12,
        padding: "8px 14px",
        borderRadius: 10,
        background: "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)",
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontSize: "0.85rem",
        fontWeight: 600,
        "&:hover": {
            background: "linear-gradient(135deg, #eee 0%, #e0e0e0 100%)",
        },
    },
    loadingBox: {
        padding: 40,
        textAlign: "center",
    },
    actions: {
        padding: "12px 24px 20px",
        borderTop: "1px solid",
        borderColor: theme.palette.divider,
    },
    btnClose: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
    },
    btnExpand: {
        textTransform: "none",
        fontSize: "0.8rem",
        marginTop: 4,
        marginBottom: 8,
    },
    timelineWrap: {
        marginTop: 8,
        paddingLeft: 8,
        borderLeft: "2px solid",
        borderColor: "rgba(0,0,0,0.12)",
    },
    timelineItem: {
        position: "relative",
        paddingBottom: 14,
        "&:last-child": { paddingBottom: 0 },
    },
    timelineDot: {
        position: "absolute",
        left: -10,
        top: 4,
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: theme.palette.primary.main,
    },
    timelineDotPast: {
        background: "#4caf50",
    },
    timelineContent: {
        marginLeft: 8,
    },
    timelineLabel: {
        fontSize: "0.8rem",
        fontWeight: 600,
        marginBottom: 2,
    },
    timelineMeta: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
    },
}));

const STATUS_ORDER = [
    "CREATED", "ASSIGNED", "STARTED", "AT_PICKUP", "PICKED_UP",
    "EN_ROUTE", "AT_DELIVERY", "DELIVERED", "COMPLETED", "CANCELLED",
];

export default function OrderDetailsDialog({ open, onClose, orderId, trackingBaseUrl }) {
    const classes = useStyles();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showTimeline, setShowTimeline] = useState(false);

    const fetchOrder = useCallback((showLoading = true) => {
        if (!orderId) return;
        if (showLoading) setLoading(true);
        api.get(`api/allexpress/orders/${orderId}`)
            .then(({ data }) => {
                if (data?.data) setOrder(data.data);
                setError(null);
            })
            .catch((err) => {
                if (showLoading) setError(err?.response?.status === 404 ? "Orden no encontrada" : "Error al cargar la orden");
            })
            .finally(() => {
                if (showLoading) setLoading(false);
            });
    }, [orderId]);

    useEffect(() => {
        if (!open || !orderId) {
            setOrder(null);
            setError(null);
            setShowTimeline(false);
            return;
        }
        fetchOrder(true);
    }, [open, orderId, fetchOrder]);

    useEffect(() => {
        if (!open || !orderId) return;
        const interval = setInterval(() => fetchOrder(false), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [open, orderId, fetchOrder]);

    const pickup = order?.locations?.find((l) => l.type === "PICKUP");
    const delivery = order?.locations?.find((l) => l.type === "DELIVERY");
    const currencySymbol = order?.currency?.symbol || "C$";
    const deliveryFee = order?.deliveryFee != null ? Number(order.deliveryFee) : null;
    const statusLabel = order ? getOrderStatusLabel(order.currentStatus) : "";
    const isCancelled = order?.currentStatus === "CANCELLED";
    const isComplete = order?.currentStatus === "COMPLETED" || order?.currentStatus === "DELIVERED";
    const statusChipClass = isCancelled
        ? classes.statusCancel
        : isComplete
        ? classes.statusDone
        : classes.statusActive;
    const trackingUrl =
        order?.trackingNumber && trackingBaseUrl
            ? `${trackingBaseUrl.replace(/\/$/, "")}/rastreo?code=${order.trackingNumber}`
            : "";

    return (
        <Dialog open={open} onClose={onClose} className={classes.root}>
            <DialogTitle className={classes.title} disableTypography>
                <LocalShippingOutlined style={{ color: "var(--primary)", fontSize: 26 }} />
                <span>Detalle de la orden</span>
            </DialogTitle>
            <DialogContent className={classes.content}>
                {loading && (
                    <Box className={classes.loadingBox}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                            Cargando...
                        </Typography>
                    </Box>
                )}
                {error && !loading && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}
                {order && !loading && (
                    <>
                        <div className={classes.section}>
                            <div className={classes.refRow}>
                                <Typography className={classes.refCode}>
                                    Ref: {order.referenceCode || order.id}
                                </Typography>
                                <span className={`${classes.statusChip} ${statusChipClass}`}>
                                    {statusLabel}
                                </span>
                            </div>
                            <Typography variant="caption" color="textSecondary">
                                Código de rastreo: <strong>{order.trackingNumber || "—"}</strong>
                            </Typography>
                        </div>

                        <div className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                <PlaceOutlined style={{ fontSize: 14 }} />
                                Ubicaciones
                            </Typography>
                            {pickup && (
                                <div className={`${classes.card} ${classes.cardPickup}`}>
                                    <div className={classes.cardLabel}>Recogida</div>
                                    <div className={classes.contactName}>
                                        {pickup.contactName || "—"}
                                    </div>
                                    <div className={classes.address}>
                                        {pickup.addressText || "Sin dirección"}
                                    </div>
                                    {(pickup.phoneNumber || pickup.contactPhone) && (
                                        <div className={classes.phone}>
                                            Tel: {pickup.phoneNumber
                                                ? `+${pickup.phoneNumber.countryCode || ""} ${pickup.phoneNumber.phoneNumber || ""}`
                                                : pickup.contactPhone}
                                        </div>
                                    )}
                                    {pickup.notes && (
                                        <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic", display: "block", marginTop: 4 }}>
                                            {pickup.notes}
                                        </Typography>
                                    )}
                                </div>
                            )}
                            {delivery && (
                                <div className={`${classes.card} ${classes.cardDelivery}`}>
                                    <div className={classes.cardLabel}>Entrega</div>
                                    <div className={classes.contactName}>
                                        {delivery.contactName || "—"}
                                    </div>
                                    <div className={classes.address}>
                                        {delivery.addressText || "Sin dirección"}
                                    </div>
                                    {(delivery.phoneNumber || delivery.contactPhone) && (
                                        <div className={classes.phone}>
                                            Tel: {delivery.phoneNumber
                                                ? `+${delivery.phoneNumber.countryCode || ""} ${delivery.phoneNumber.phoneNumber || ""}`
                                                : delivery.contactPhone}
                                        </div>
                                    )}
                                    {delivery.notes && (
                                        <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic", display: "block", marginTop: 4 }}>
                                            {delivery.notes}
                                        </Typography>
                                    )}
                                </div>
                            )}
                            {!pickup && !delivery && (
                                <Typography variant="body2" color="textSecondary">
                                    Sin datos de ubicación
                                </Typography>
                            )}
                        </div>

                        {deliveryFee != null && (
                            <div className={classes.section}>
                                <div className={classes.costRow}>
                                    <span className={classes.costLabel}>Tarifa de entrega</span>
                                    <span className={classes.costValue}>
                                        {currencySymbol}{deliveryFee.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {trackingUrl && (
                            <a
                                href={trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.trackingLink}
                            >
                                <LocalShippingOutlined style={{ fontSize: 18 }} />
                                Ver rastreo en vivo
                            </a>
                        )}

                        <div className={classes.section}>
                            <Button
                                size="small"
                                startIcon={showTimeline ? <ExpandMore style={{ transform: "rotate(180deg)" }} /> : <Timeline />}
                                onClick={() => setShowTimeline((v) => !v)}
                                className={classes.btnExpand}
                                fullWidth
                            >
                                {showTimeline ? "Ocultar cronología" : "Ver cronología"}
                            </Button>
                            <Collapse in={showTimeline}>
                                <div className={classes.timelineWrap}>
                                    {(order.statusHistory && order.statusHistory.length > 0
                                        ? [...order.statusHistory].sort(
                                            (a, b) => new Date(a.changedAt || a.createdAt) - new Date(b.changedAt || b.createdAt)
                                        )
                                        : []
                                    ).map((entry, idx) => {
                                        const isPast = STATUS_ORDER.indexOf(entry.status) <= STATUS_ORDER.indexOf(order.currentStatus);
                                        return (
                                            <div key={idx} className={classes.timelineItem}>
                                                <div className={`${classes.timelineDot} ${isPast ? classes.timelineDotPast : ""}`} />
                                                <div className={classes.timelineContent}>
                                                    <div className={classes.timelineLabel}>
                                                        {getOrderStatusLabel(entry.status)}
                                                    </div>
                                                    <div className={classes.timelineMeta}>
                                                        {formatTime(entry.changedAt || entry.createdAt)}
                                                        {entry.changedByUser && (
                                                            <span>
                                                                {" · "}
                                                                {entry.changedByUser.firstName} {entry.changedByUser.lastName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(!order.statusHistory || order.statusHistory.length === 0) && (
                                        <Typography variant="body2" color="textSecondary">
                                            Sin historial de estados
                                        </Typography>
                                    )}
                                </div>
                            </Collapse>
                        </div>
                    </>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={onClose} color="primary" variant="outlined" className={classes.btnClose}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
